import { trackEvent } from '@cx-web/feature-tracking/dataLayer';
import { REPUB_CONFIG } from '@cx-web/repub-config';

import { type Repub } from '@cx-modules/constants';

import { actions, events, views } from './constants';

export type LowercaseSnakeCaseString<T extends string> =
  T extends Lowercase<T>
    ? T extends `${string}_${string}`
      ? string extends T
        ? never
        : T
      : string extends T
        ? never
        : T extends `${string} ${string}`
          ? never
          : T
    : never;

// NOTE: the `category` arg on the four track* helpers below (e.g. 'auth_modal')
// is reserved for future GA4 segmentation and is intentionally not forwarded to
// trackEvent yet. The LowercaseSnakeCaseString type still enforces snake_case at
// call sites.
export const trackLoginSuccess = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { repub, userId }: { repub: Repub; userId: string },
) => {
  trackEvent(events.LOG_IN, {
    eventAction: actions.LOGIN_SUCCESS,
    eventLabel: 'Login Success',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.LOGIN,
    userId,
  });
};

export const trackLoginFailure = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { error, repub }: { error: string; repub: Repub },
) => {
  trackEvent(events.LOG_IN_ERROR, {
    eventAction: actions.LOGIN_ERROR,
    eventLabel: 'Login Error',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.LOGIN,
    loginError: error,
  });
};

export const trackSignupSuccess = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { repub, userId }: { repub: Repub; userId: string },
) => {
  trackEvent(events.ACCOUNT_CREATED, {
    eventAction: actions.SIGN_UP_SUCCESS,
    eventLabel: 'Signup Success',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.SIGN_UP,
    userId,
  });
};

export const trackSignupFailure = <T extends string>(
  category: LowercaseSnakeCaseString<T>, // e.g. 'auth_modal'
  { error, repub }: { error: string; repub: Repub },
) => {
  trackEvent(events.ACCOUNT_CREATED_ERROR, {
    eventAction: actions.SIGN_UP_ERROR,
    eventLabel: 'Signup Error',
    method: REPUB_CONFIG[repub]?.tracking.authProvider,
    view: views.SIGN_UP,
    signupError: error,
  });
};
